import { createResponse } from "./cloudFunctions"
import {
  getModel,
  getAIUsageParam,
  convertToMessages,
  FUNCTION_GET_QUESTIONS_COMMON,
  FUNCTION_GET_MODEL_QUESTIONS,
  FUNCTION_GET_EXTENDED_QUESTIONS,
  MAX_MODEL_QUESTIONS,
  MAX_OPEN_QUESTIONS,
  getObjectiveAndContextMessages,
  GPT_5,
} from "./chatGenerationServices"
import * as palette from "../../components/symbols/palette"
import { v4 as uuidv4 } from "uuid"
import suggestViews from "./schemas/suggest_views.json"
import getQuestionsPlain from "./schemas/get_questions_plain.json"
import getQuestions from "./schemas/get_questions.json"

// ... unchanged imports ...

const generateQuestions = async ({
  viewSet,
  views,
  questionsTabFiles,
  setMessage,
  setPercentComplete,
  roles,
}) => {
  const gptModelCommon = getModel({
    roles,
    funcName: FUNCTION_GET_QUESTIONS_COMMON,
  })
  const gptModelModelQs = getModel({
    roles,
    funcName: FUNCTION_GET_MODEL_QUESTIONS,
  })
  const gptModelOpenQs = getModel({
    roles,
    funcName: FUNCTION_GET_EXTENDED_QUESTIONS,
  })

  const DEFAULT_NUM_QUESTIONS = 15
  const maxModelQuestions =
    getAIUsageParam({ roles, paramName: MAX_MODEL_QUESTIONS }) ||
    DEFAULT_NUM_QUESTIONS
  const maxOpenQuestions =
    getAIUsageParam({ roles, paramName: MAX_OPEN_QUESTIONS }) ||
    DEFAULT_NUM_QUESTIONS

  const pinnedElementQuestions =
    viewSet.view_questions?.filter((q) => q.layer && q.pinned) || []
  const pinnedOpenQuestions =
    viewSet.view_questions?.filter((q) => !q.layer && q.pinned) || []

  const elementQuestionsToCreate = Math.max(
    0,
    maxModelQuestions - pinnedElementQuestions.length
  )
  const openQuestionsToCreate = Math.max(
    0,
    maxOpenQuestions - pinnedOpenQuestions.length
  )

  const vectorStoreIds = questionsTabFiles.map((item) => item.id)
  const tools = vectorStoreIds.length
    ? [{ type: "file_search", vector_store_ids: vectorStoreIds }]
    : undefined

    console.log("using tools", tools)

  const initialMessages = getObjectiveAndContextMessages({ viewSet })
  const basePrompt = convertToMessages(initialMessages).map((m) =>
    typeof m === "string" ? m : m.text
  )

  // Helpers to normalize and dedupe
  const normalizeLayered = (arr = []) =>
    arr.map((q) => ({
      ...q,
      question_id: `question-${uuidv4()}`,
      // prefer layer derived from first element type (old behavior)
      layer:
        (q.element_types?.length
          ? palette.getElementType(q.element_types[0])?.layer?.name
          : undefined) || q.layer,
    }))

  const normalizeOpen = (arr = []) =>
    arr.map((q) => ({
      ...q,
      question_id: `question-${uuidv4()}`,
    }))

  const dedupeByQuestionText = (arr = []) => {
    const seen = new Set()
    return arr.filter((q) => {
      const key = (q.question || q.title || "").trim().toLowerCase()
      if (!key) return true // keep if no stable text key
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  // ----------------
  // 1️⃣ Element (layered) questions
  // ----------------
  setMessage?.("Generating model-layer questions...(step 1 of 2)")
  setPercentComplete?.(30)

  const elementQsPrompt = [
    ...basePrompt,
    "Given the objective, propose important questions to answer.",
    "Ensure coverage across ArchiMate layers; pick the single *best* element type per question.",
    `Create at least ${elementQuestionsToCreate} unique questions.`,
    "Provide a concise rationale (<=100 words) for each.",
    "Include several Strategy-layer questions (Capabilities, Courses of Action, Resources).",
  ]

  if (pinnedElementQuestions.length) {
    elementQsPrompt.push(
      "Do not duplicate any of these:",
      ...pinnedElementQuestions.map((q) => `- ${q.question}`)
    )
  }
  if (tools) {
    elementQsPrompt.push(
      "Consult the attached file search corpus; cite files briefly where directly relevant."
    )
  }

  const elementQsResult = await createResponse({
    prompt: elementQsPrompt.join("\n\n"),
    model: gptModelCommon,
    tools,
    reasoning: { effort: "medium" },
    jsonSchema: getQuestions,
    store: true,
  })

  const elementResponseId = elementQsResult?.response?.id
  let generatedElementQs = []
  try {
    const parsed = JSON.parse(elementQsResult?.response?.output_text || "")
    generatedElementQs = parsed.questions || []
  } catch (e) {
    console.warn("Failed to parse element questions JSON", e)
  }

  // ----------------
  // 2️⃣ Open (non-layered) questions — passes previous_response_id
  // ----------------
  setMessage?.("Generating open questions...(step 2 of 2)")
  setPercentComplete?.(60)

  const openQsPrompt = [
    "List important open questions (not tied to a specific ArchiMate layer) that help achieve the objective.",
    `Create at least ${openQuestionsToCreate} unique open questions.`,
    "Avoid duplicating any existing open questions.",
  ]

  if (pinnedOpenQuestions.length) {
    openQsPrompt.push(
      "Do not duplicate any of these:",
      ...pinnedOpenQuestions.map((q) => `- ${q.question}`)
    )
  }
  if (tools) {
    openQsPrompt.push("Consult the file search corpus and cite where relevant.")
  }

  const openQsResult = await createResponse({
    prompt: openQsPrompt.join("\n\n"),
    model: gptModelModelQs,
    tools,
    reasoning: { effort: "medium" },
    jsonSchema: getQuestionsPlain,
    store: true,
    previous_response_id: elementResponseId,
  })

  //const openResponseId = openQsResult?.response?.id
  let generatedOpenQs = []
  try {
    const parsed = JSON.parse(openQsResult?.response?.output_text || "")
    generatedOpenQs = parsed.questions || []
  } catch (e) {
    console.warn("Failed to parse open questions JSON", e)
  }

  // ----------------
  // 3️⃣ Suggested views — passes previous_response_id again
  // ----------------
  //setMessage?.("Suggesting views...(step 3 of 3)")
  //setPercentComplete?.(85)

  // const allQsForSuggest = [
  //   ...pinnedElementQuestions,
  //   ...generatedElementQs,
  //   ...pinnedOpenQuestions,
  //   ...generatedOpenQs,
  // ]

  // const suggestViewsPrompt = [
  //   "Based on the following questions, suggest a set of views that would best help answer them:",
  //   ...allQsForSuggest.map((q, i) => `Q${i + 1}. ${q.question || q}`),
  // ]

  // const suggestViewsResult = await createResponse({
  //   prompt: suggestViewsPrompt.join("\n\n"),
  //   model: "gpt-5-mini", // or gptModelCommon
  //   tools,
  //   reasoning: { effort: "low" },
  //   jsonSchema: suggestViews,
  //   store: true,
  //   previous_response_id: openResponseId,
  // })

  // let suggestedViews = []
  // try {
  //   const parsed = JSON.parse(suggestViewsResult?.response?.output_text || "")
  //   suggestedViews = parsed.views || []
  // } catch (e) {
  //   console.warn("Failed to parse suggested views JSON", e)
  // }

  // ✅ FIX: combine both sets like the old version did
  const normalizedLayered = normalizeLayered(generatedElementQs)
  const normalizedOpen = normalizeOpen(generatedOpenQs)
  let layerBasedQuestions = dedupeByQuestionText([
    ...normalizedLayered,
    ...normalizedOpen,
  ])

  const viewContext = viewSet.view_context

  setPercentComplete?.(100)

  return {
    suggestedViews: [], // keep empty per your legacy contract
    viewContext,
    viewQuestions: layerBasedQuestions, // now includes BOTH sets
  }
}

export { generateQuestions }
